import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Target, TrendingUp, Lightbulb } from 'lucide-react';

const modules = [
  {
    id: 1,
    title: 'Diagnose & Frame High-Leverage Unknowns',
    objective: 'Rewrite vague questions into precise, high-leverage unknowns',
    tool: '3-Point OQF Checklist',
    pages: 6,
    color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]',
    icon: Target
  },
  {
    id: 2,
    title: 'Surface & Label Assumptions',
    objective: 'Identify hidden assumptions and label their evidence quality',
    tool: 'Evidence-Quality Scale',
    pages: 4,
    color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]',
    icon: Lightbulb
  },
  {
    id: 3,
    title: 'Define the Path of Inquiry',
    objective: 'Design a Smallest Decisive Experiment (SDE) to test assumptions',
    tool: '3 SDE Criteria',
    pages: 5,
    color: 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]',
    icon: TrendingUp
  },
  {
    id: 4,
    title: 'Prioritize Open Questions',
    objective: 'Rank questions using the OQF Prioritization Rubric',
    tool: 'Prioritization Rubric',
    pages: 5,
    color: 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]',
    icon: Target
  },
  {
    id: 5,
    title: 'Maintain an Open Questions Register',
    objective: 'Populate a shared OQ Register to track learning',
    tool: 'OQF Register Template',
    pages: 5,
    color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]',
    icon: BookOpen
  },
  {
    id: 6,
    title: 'Measure Clarity Acceleration',
    objective: 'Calculate and interpret clarity metrics',
    tool: 'Clarity Metrics Calculator',
    pages: 5,
    color: 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]',
    icon: TrendingUp
  },
  {
    id: 7,
    title: 'Make OQF a Habit',
    objective: 'Apply OQF to daily work and life',
    tool: 'OQF-Lite Loop & Playbook',
    pages: 5,
    color: 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)]',
    icon: Lightbulb
  }
];

export default function OQFHome() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <header className="bg-[var(--bg-raised)] border-b border-[var(--rule)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[var(--text-pri)]">
                The Open Questions Framework
              </h1>
              <p className="text-[var(--text-sec)] mt-1">
                Your Workbook for Moving from Uncertainty to Clarity
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <div className="mb-12 bg-[var(--slate-dim)] border-l-4 border-[var(--slate)] p-6">
          <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-3">
            🚀 Welcome to the OQF Workshop
          </h2>
          <p className="text-[var(--text-sec)] mb-3">
            Welcome. The next few hours are dedicated to learning a single, powerful skill: how to get to the heart of the matter, faster.
          </p>
          <p className="text-[var(--text-sec)] mb-3">
            We've all been in meetings that spin in circles, on projects that feel "stuck" in a fog of ambiguity, and in debates that are full of opinions but empty of evidence. These moments of friction aren't just frustrating; they're expensive. They cost us time, money, and morale.
          </p>
          <p className="text-[var(--text-sec)] mb-3">
            The <strong>Open Questions Framework (OQF)</strong> is a structured method for cutting through that fog. It's a repeatable system for replacing ambiguity and untested assumptions with clarity and directed learning.
          </p>
          <div className="mt-4 p-4 bg-[var(--bg-raised)] rounded border border-[var(--slate)]">
            <h3 className="font-semibold text-[var(--text-pri)] mb-2">How to Use This Workbook</h3>
            <p className="text-[var(--text-sec)] text-sm">
              This workbook is your companion and your personal workspace. It's not just for notes—it's for <em>work</em>. Inside, you'll find all the key tools, templates, and activities we'll use in the workshop. The real learning doesn't come from listening; it comes from <em>doing</em>. Don't be afraid to write, scribble, sketch, and challenge the ideas on these pages.
            </p>
            <p className="text-[var(--text-sec)] text-sm mt-2">
              By the end of this course, you won't just <em>understand</em> the framework. You'll have <em>used</em> it.
            </p>
          </div>
        </div>

        {/* Modules Grid */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2 text-center">
            The 7 Modules
          </h2>
          <p className="text-[var(--text-sec)] text-center mb-8">
            Work through each module sequentially to master the Open Questions Framework
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module) => {
              const Icon = module.icon;
              return (
                <Link
                  key={module.id}
                  to={`/elevation/workbook/oqf/module-${module.id}`}
                  className="group block h-full"
                >
                  <div className={`h-full p-6  border-2 transition-all duration-200 group-hover:shadow-lg flex flex-col ${module.color}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-[var(--bg-raised)]">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="text-xs font-semibold opacity-75 mb-1">
                          Module {module.id}
                        </div>
                        <h3 className="text-lg font-bold">
                          {module.title}
                        </h3>
                      </div>
                    </div>
                    
                    <div className="flex-grow space-y-2 mb-4">
                      <div>
                        <p className="text-sm font-medium mb-1">Objective:</p>
                        <p className="text-sm opacity-90">
                          {module.objective}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium mb-1">Key Tool:</p>
                        <p className="text-sm opacity-90">
                          {module.tool}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-current opacity-20">
                      <span className="text-xs">
                        {module.pages} pages
                      </span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Quick Reference */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <h3 className="text-xl font-bold text-[var(--text-pri)] mb-4">
            Quick Reference
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-[var(--text-pri)] mb-2">For Individuals</h4>
              <ol className="space-y-2 text-[var(--text-sec)] text-sm">
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-[var(--text-ter)]">1.</span>
                  <span>Start with Module 1: Diagnose & Frame</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-[var(--text-ter)]">2.</span>
                  <span>Work through each module sequentially</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-[var(--text-ter)]">3.</span>
                  <span>Complete all activities in your workbook</span>
                </li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold text-[var(--text-pri)] mb-2">For Teams</h4>
              <ol className="space-y-2 text-[var(--text-sec)] text-sm">
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">1.</span>
                  <span>Follow along during the workshop</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">2.</span>
                  <span>Work through activities together</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">3.</span>
                  <span>Share insights and discuss as a group</span>
                </li>
              </ol>
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <Link
              to="/elevation/workbook/oqf/module-1"
              className="inline-flex items-center gap-2 bg-[var(--slate)] text-white px-6 py-3 font-semibold hover:bg-[var(--slate)] transition-colors"
            >
              Start with Module 1
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}






